import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';

import type { MutationUpdaterInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
	UNRESOLVE_INLINE_REACTION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { type ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-utils/entry-points/enum.ts';
import { ViewValues } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { CommentType } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getLogger } from '@confluence/logger';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { i18n as inlineCommentsI18n } from './inlineCommentsi18n.ts';

const inlineReactionsI18n = defineMessages({
	resolvedFlagText: {
		id: 'inline-comments-common.reaction.resolved.flag.text',
		defaultMessage: 'Reaction resolved',
		description: 'Text in flag when current user resolves a reaction',
	},
	goToReactionCTA: {
		id: 'inline-comments-common.reaction.reopened.flag.cta.gotoreaction',
		defaultMessage: 'Go to reaction',
		description:
			'CTA button text displayed in Flag for users to open the reaction in the comments panel that was just resolved or reopened.',
	},
	undoCTA: {
		id: 'inline-comments-common.reaction.reopened.flag.cta.undo',
		defaultMessage: 'Undo',
		description:
			'CTA button text displayed in Flag for users to undo the reaction resolved or reopened action.',
	},
});

export type ReopenCommentDependencies<TData> = {
	reopenInlineCommentFn: (params: {
		variables: { commentId: string; resolved: boolean };
		update?: MutationUpdaterInterop<TData>;
	}) => Promise<any>;
	getUpdateFn?: (
		onReopenCacheError: (reopenCacheError: Error) => void,
	) => MutationUpdaterInterop<TData>;
	commentId: string;
	annotationId: string;
	reopenParentComment: (params: {
		threadKey: string;
		commentType: CommentType;
		parentCommentId?: string;
	}) => void;
	setOrderedActiveAnnotationIdList: (list: string[]) => void;
	orderedActiveAnnotationIdList: Array<{ threadKey: string }>;
	highlightReopenedComment: (id: string) => void;
	/**
	 * Called after a successful reopen (Undo) so callers can roll back any
	 * optimistic state set during the resolve. For inline reactions this clears
	 * the entry from `optimisticResolvedMarkerRefs` so the highlight reappears.
	 */
	onReopen?: () => void;
	isEditor: boolean;
	eventEmitter: any;
	setActiveHighlight: (id: string | null) => void;
	experienceTracker: ExperienceTrackerAPI;
	isInlineReaction?: boolean;
};

const logger = getLogger('annotation-provider-inline-comment');

export const createHandleReopenComment = <TData>(
	dependencies: ReopenCommentDependencies<TData>,
) => {
	const {
		reopenInlineCommentFn,
		getUpdateFn,
		commentId,
		annotationId,
		reopenParentComment,
		setOrderedActiveAnnotationIdList,
		orderedActiveAnnotationIdList,
		highlightReopenedComment,
		onReopen,
		isEditor,
		eventEmitter,
		setActiveHighlight,
		experienceTracker,
		isInlineReaction,
	} = dependencies;

	const unresolveExperienceName = isInlineReaction
		? UNRESOLVE_INLINE_REACTION_EXPERIENCE
		: UNRESOLVE_INLINE_COMMENT_EXPERIENCE;

	return (): Promise<void> => {
		return reopenInlineCommentFn({
			variables: { commentId, resolved: false },
			update: getUpdateFn?.((cacheError: Error) => {
				experienceTracker.stopOnError({
					name: unresolveExperienceName,
					error: cacheError,
				});
			}),
		})
			.then(() => {
				reopenParentComment({
					threadKey: annotationId,
					commentType: CommentType.INLINE,
					parentCommentId: commentId,
				});
				setOrderedActiveAnnotationIdList([
					...orderedActiveAnnotationIdList.map((status) => status.threadKey),
					annotationId,
				]);
				highlightReopenedComment(annotationId);

				// Roll back caller-side optimistic state (e.g. optimisticResolvedMarkerRefs)
				// so the annotation highlight reappears after a successful Undo.
				onReopen?.();

				if (isEditor) {
					eventEmitter.emit('unresolve', annotationId);
					eventEmitter.emit('setselectedannotation', annotationId);
				} else {
					// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
					const commentElement = document.getElementById(annotationId);
					eventEmitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
						annotationIds: [annotationId],
						eventTarget: commentElement,
					});
					setActiveHighlight(annotationId);
				}
				experienceTracker.succeed({
					name: unresolveExperienceName,
				});
			})
			.catch((err: any) => {
				logger.error`An error occurred when reopening a comment ${err}`;

				experienceTracker.stopOnError({
					name: unresolveExperienceName,
					error: err,
				});
			});
	};
};

export const showResolvedCommentFlag = ({
	commentId,
	experienceTracker,
	flags,
	mode,
	annotationId,
	handleReopenComment,
	formatMessage,
	showCommentsPanel,
	setCurrentView,
	isCommentsPanelShown,
	setCurrentlySelectedThreadKey,
	isInlineReaction,
}: {
	commentId: string;
	experienceTracker: ExperienceTrackerAPI;
	flags: FlagsStateContainer;
	mode: PageMode;
	annotationId: string;
	handleReopenComment: () => void;
	formatMessage: IntlShape['formatMessage'];
	showCommentsPanel: (options: { openWithView: ViewValues }) => void;
	setCurrentView: (view: ViewValues) => void;
	isCommentsPanelShown: boolean;
	setCurrentlySelectedThreadKey: (threadKey: string) => void;
	isInlineReaction?: boolean;
}): void => {
	void flags?.showSuccessCircleFlag({
		id: commentId,
		title: formatMessage(
			isInlineReaction ? inlineReactionsI18n.resolvedFlagText : inlineCommentsI18n.resolvedFlagText,
		),
		isAutoDismiss: true,
		actions: [
			{
				content: formatMessage(
					isInlineReaction
						? inlineReactionsI18n.goToReactionCTA
						: inlineCommentsI18n.goToCommentCTA,
				),
				onClick: () => {
					if (!isCommentsPanelShown) {
						setCurrentlySelectedThreadKey(annotationId);
						showCommentsPanel({ openWithView: ViewValues.RESOLVED });
					} else {
						setCurrentView(ViewValues.RESOLVED);
					}

					if (expValEquals('confluence_comments_scrollto_resolve', 'isEnabled', true)) {
						void handleCommentsPanelNavigationWithViewChange({
							scrollBehavior: ScrollBehavior.SMOOTH,
							context: NavigationContext.PANEL,
							block: 'center',
							annotationId,
							commentType: 'INLINE',
							annotationClicked: true,
							expectedView: ViewValues.RESOLVED,
						});
					}

					void flags.hideFlag(flags.state.flags[0]?.id);
				},
			},
			{
				content: formatMessage(
					isInlineReaction ? inlineReactionsI18n.undoCTA : inlineCommentsI18n.undoCTA,
				),
				onClick: () => {
					experienceTracker.start({
						name: isInlineReaction
							? UNRESOLVE_INLINE_REACTION_EXPERIENCE
							: UNRESOLVE_INLINE_COMMENT_EXPERIENCE,
						id: commentId,
						attributes: {
							mode,
							framework: InlineCommentFramework.ANNOTATION_PROVIDER,
							annotationId,
						},
					});

					handleReopenComment();

					void flags.hideFlag(flags.state.flags[0]?.id);
				},
			},
		],
	});
};
